/* @flow */
import { css } from 'linaria';
import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import Colors from '../../constants/Colors';
import { RequestInAppBlock, ReviewBlock, ThreeFeaturesBlock } from '../components';
import {
  BodyText,
  Card,
  Container,
  Grid,
  GridCell,
  Icon,
  Image,
  InlineFeature,
  SectionTitle,
  Spacer,
  Title,
} from '../../components/core';
import { PageBody } from '../../components/chrome';
import Responsive from '../../constants/Responsive';
import { ResponsiveEstimateForm } from '../../components/EstimateForm';
import Reviews, { getReviewTagged } from '../../../data/Reviews';
import type { ServicesPageChromeMetadata } from './ServicesPageChrome';
import tripRequests from '../../../redux/trip-requests';
import type { UseCase } from '../../../data/TripRequests';

const DonationLocationStyles = {
  Card: css`
    background-color: ${Colors.opacity('#F8F8F9', 0.4)};
    text-align: center;
    padding-top: 0;
    margin-top: 40px;
  `,
  Logo: css`
    margin-bottom: 24px;
    margin-top: -40px;
  `,
  Link: css`
    vertical-align: middle;
  `,
  LinkIcon: css`
    margin-left: 8px;
  `,
};

function DonationLocation({
  title,
  logo,
  description,
  url,
}: {
  title: string,
  logo: any,
  description: string,
  url: string,
}) {
  return (
    <Card className={DonationLocationStyles.Card}>
      <div className={DonationLocationStyles.Logo}>{logo}</div>
      <Title size="26" weight="bold">
        {title}
      </Title>
      <BodyText size="15">{description}</BodyText>
      <a href={url} className={DonationLocationStyles.Link}>
        <BodyText size="20" align="center" weight="medium" color={Colors.Violet}>
          Find drop-off center{' '}
          <Icon className={DonationLocationStyles.LinkIcon} name="arrow-big-violet-right" />
        </BodyText>
      </a>
    </Card>
  );
}

class DonateAndJunkPage extends React.Component<{
  setNewTripRequestUseCase: (useCase: UseCase) => void,
}> {
  static metadata = {
    title: 'Donations',
    description:
      'Connect with movers and a truck to help with your Goodwill and Salvation Army furniture delivery donations same day!',
  };

  static servicesMetadata: ServicesPageChromeMetadata = {
    illustration: 'luggers-loading-junk',
    id: 'donations',
  };

  componentDidMount() {
    const { setNewTripRequestUseCase } = this.props;
    setNewTripRequestUseCase('donation');
  }

  render() {
    return (
      <PageBody>
        <Container padded={true} align="center">
          <SectionTitle title="Donation pick up and delivery with a tap" />
          <BodyText
            size="16"
            align="center"
            style={{ maxWidth: '630px', marginLeft: 'auto', marginRight: 'auto' }}>
            Connect with movers and a truck to help with your Goodwill and Salvation Army delivery
            donations same day!
          </BodyText>
          <Spacer size={80} />
          <ThreeFeaturesBlock
            features={[
              {
                title: 'Any donation center',
                description:
                  'Find your nearest drop-off station of your preferred charity or other organization.',
                icon: 'map-alt1',
              },
              {
                title: 'On your schedule',
                description:
                  'Two Luggers and a truck will pick up your unwanted items when you’re ready.',
                icon: 'pick-a-time',
              },
              {
                title: 'Donated for you',
                description:
                  'We’ll deliver your unwanted to your nearest donation center of choice.',
                icon: 'stay-at-home',
              },
            ]}
          />
        </Container>
        <Spacer size={120} />
        <Container size="large" padded={true}>
          <ResponsiveEstimateForm />
        </Container>
        <Spacer size={120} />
        <Container padded={true}>
          <SectionTitle
            title="Donate your items"
            subtitle="We can donate your items to any charity of your preference"
          />
          <Spacer size={80} />
          <Grid equalHeight={true}>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="furniture" />}
                title="Furniture"
                description="Dressers, tables, sofas, bed frames, (except waterbed frames), etc."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="clothing" />}
                title="Clothing"
                description="Shirts, sweaters, jeans, shoes, boots, suits, jackets, jewelry, accessories, etc."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="boombox" />}
                title="Electronics"
                description="Personal computers, televisions, stereos, radios, VCRs, DVD players, etc."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="dinnerware" />}
                title="Housewares"
                description="Dishes, glassware, silverware, pots & pans, utensils, baskets, picture frames, etc."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="toys" />}
                title="Toys & sports equipment"
                description="Dolls, stuffed animals, bicycles, golf clubs, tennis racquets, treadmills, etc."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="tools" />}
                title="Hand tools"
                description="Drills, screwdrivers, wrenches, mechinical tools, saws, shovels, workbenches, etc."
              />
            </GridCell>
          </Grid>
          <Spacer size={60} />
          <Grid className={Styles.DonationLocationGrid}>
            <GridCell
              size={Responsive('size12of12', 'size6of12')}
              className={Styles.DonationLocationGridCell}>
              <DonationLocation
                logo={
                  <Image
                    src={require('../../../assets/images/logos/goodwill-small.png')}
                    src2x={require('../../../assets/images/logos/goodwill-small_2x.png')}
                    style={{ width: 61 }}
                  />
                }
                title="We ❤️ Goodwill"
                description="“It’s simple. When you donate your stuff to Goodwill, you create opportunities for individuals in your community looking to find a job and build skills, including veterans and military families, single mothers and many others.”"
                url="http://www.goodwill.org/locator/"
              />
            </GridCell>
            <GridCell
              size={Responsive('size12of12', 'size6of12')}
              className={Styles.DonationLocationGridCell}>
              <DonationLocation
                logo={
                  <Image
                    src={require('../../../assets/images/logos/salvation-army-small.png')}
                    src2x={require('../../../assets/images/logos/salvation-army-small_2x.png')}
                    style={{ width: 67 }}
                  />
                }
                title="We ❤️ Salvation Army"
                description="“When you donate goods to The Salvation Army, those items are then sold at our Family Stores. And the proceeds are used to fund our Adult Rehabilitation Centers, where those in the grip of addiction find help, hope, and a second chance at life.”"
                url="http://www.salvationarmyusa.org/usn/plugins/gdosCenterSearch?query=94117&mode=query_1&limit=100"
              />
            </GridCell>
          </Grid>
        </Container>
        <Container size="large" padded={true}>
          <Spacer size={200} color={Colors.Divider} />
        </Container>
        <Container padded={true}>
          <RequestInAppBlock />
        </Container>
        <Container size="large" padded={true}>
          <Spacer size={200} color={Colors.Divider} />
        </Container>
        <Container padded={true}>
          <ReviewBlock review={getReviewTagged(Reviews, 'donations-page')} />
        </Container>
      </PageBody>
    );
  }
}

const Styles = {
  InlineFeature: css`
    margin-bottom: 56px;
  `,
  DonationLocationGrid: css`
    margin-left: -12px;
    margin-right: -12px;
  `,
  DonationLocationGridCell: css`
    padding: 12px;
  `,
};

export default compose(
  connect(
    null,
    dispatch => ({
      setNewTripRequestUseCase: useCase =>
        dispatch(tripRequests.actions.setNewTripRequestUseCase(useCase)),
    })
  )
)(DonateAndJunkPage);



// WEBPACK FOOTER //
// ./src/ui/pages/ServicesPage/DonationsPage.js